import { 
  Home, 
  BarChart3, 
  MessageSquare, 
  FileText, 
  Settings, 
  Users, 
  CreditCard,
  Plus,
  Download,
  Search,
  Brain,
  type LucideIcon
} from "lucide-react";

export interface Command {
  id: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  keywords: string[];
  action: () => void;
  shortcut?: string;
  category: "navigation" | "action" | "search" | "recent";
  page?: string;
}

export interface CommandGroup {
  label: string;
  commands: Command[];
}

/**
 * Create command registry with navigation and actions
 */
export function createCommandRegistry(navigate: (path: string) => void): CommandGroup[] {
  return [
    {
      label: "Navigation",
      commands: [
        {
          id: "nav-dashboard",
          label: "Dashboard",
          description: "View your overview and metrics",
          icon: Home,
          keywords: ["dashboard", "home", "overview", "metrics"],
          action: () => navigate("/dashboard"),
          shortcut: "G then D",
          category: "navigation",
          page: "/dashboard",
        },
        {
          id: "nav-analytics",
          label: "Analytics",
          description: "View detailed analytics and insights",
          icon: BarChart3,
          keywords: ["analytics", "stats", "reports", "insights"],
          action: () => navigate("/dashboard/analytics"),
          shortcut: "G then A",
          category: "navigation",
          page: "/dashboard/analytics",
        },
        {
          id: "nav-feedback",
          label: "Feedback",
          description: "View and manage feedback submissions",
          icon: MessageSquare,
          keywords: ["feedback", "submissions", "responses"],
          action: () => navigate("/dashboard/feedback"),
          shortcut: "G then F",
          category: "navigation",
          page: "/dashboard/feedback",
        },
        {
          id: "nav-pages",
          label: "Feedback Pages",
          description: "Manage your feedback collection pages",
          icon: FileText,
          keywords: ["pages", "forms", "links"],
          action: () => navigate("/dashboard/pages"),
          shortcut: "G then P",
          category: "navigation",
          page: "/dashboard/pages",
        },
        {
          id: "nav-hr-advisor",
          label: "AI Advisor",
          description: "Get AI-powered insights",
          icon: Brain,
          keywords: ["ai", "hr", "advisor", "insights", "recommendations"],
          action: () => navigate("/dashboard/hr-advisor"),
          category: "navigation",
          page: "/dashboard/hr-advisor",
        },
        {
          id: "nav-team",
          label: "Team",
          description: "Manage team members and permissions",
          icon: Users,
          keywords: ["team", "members", "users", "permissions"],
          action: () => navigate("/dashboard/team"),
          category: "navigation",
          page: "/dashboard/team",
        },
        {
          id: "nav-billing",
          label: "Billing",
          description: "Manage subscription and billing",
          icon: CreditCard,
          keywords: ["billing", "subscription", "plan", "payment"],
          action: () => navigate("/dashboard/settings?tab=billing"),
          category: "navigation",
          page: "/dashboard/settings",
        },
        {
          id: "nav-settings",
          label: "Settings",
          description: "Configure your account and preferences",
          icon: Settings,
          keywords: ["settings", "preferences", "configuration"],
          action: () => navigate("/dashboard/settings"),
          shortcut: "G then S",
          category: "navigation",
          page: "/dashboard/settings",
        },
      ],
    },
    {
      label: "Actions",
      commands: [
        {
          id: "action-create-page",
          label: "Create New Page",
          description: "Create a new feedback collection page",
          icon: Plus,
          keywords: ["create", "new", "page", "form", "add"],
          action: () => navigate("/dashboard/new"),
          shortcut: "C",
          category: "action",
        },
        {
          id: "action-export-feedback",
          label: "Export Feedback",
          description: "Export feedback data to CSV or PDF",
          icon: Download,
          keywords: ["export", "download", "csv", "pdf", "data"],
          action: () => {
            // This will be handled by the current page
            const event = new CustomEvent("command-export-feedback");
            window.dispatchEvent(event);
          },
          category: "action",
        },
      ],
    },
  ];
}

/**
 * Filter commands based on search query
 */
export function filterCommands(
  groups: CommandGroup[],
  query: string
): CommandGroup[] {
  if (!query) return groups;

  const searchLower = query.toLowerCase();

  return groups
    .map((group) => ({
      ...group,
      commands: group.commands.filter((command) => {
        const labelMatch = command.label.toLowerCase().includes(searchLower);
        const descMatch = command.description?.toLowerCase().includes(searchLower);
        const keywordMatch = command.keywords.some((keyword) =>
          keyword.toLowerCase().includes(searchLower)
        );

        return labelMatch || descMatch || keywordMatch;
      }),
    }))
    .filter((group) => group.commands.length > 0);
}
